import {
    addDays,
    addMonths,
    isAfter,
    isBefore,
    isEqual,
    startOfMonth,
    subDays,
} from "date-fns";
import { toIsoDate } from "@common/helpers/date";
import {
    DateInterval,
    OPEN_INTERVAL_END,
    ParsedWindow,
    clipDocToOptionalWindow,
    parseOptionalPeriodToWindow,
} from "./period";
import {
    coverageAwaitingRow,
    coverageMatchedRow,
    coverageRequirementAwaitingRow,
    coverageUnmatchedDoc,
} from "./coverageRows";
import {
    CollectedExtraction,
    CoverageRow,
    Period,
    RequirementKey,
    UnmatchedDoc,
} from "./types";

export interface ContinuousCoverage {
    rows: CoverageRow[];
    unmatched: UnmatchedDoc[];
    isCovered: boolean;
}

interface ClippedDoc {
    doc: CollectedExtraction;
    interval: DateInterval;
}

function awaitingPeriodFromWindow(window: ParsedWindow): Period | null {
    if (window.start === null && window.end === null) {
        return null;
    }
    return {
        start: window.start ? toIsoDate(window.start) : null,
        end: window.end ? toIsoDate(window.end) : null,
    };
}

function matchedPeriodFromInterval(
    doc: CollectedExtraction,
    interval: DateInterval,
): Period {
    return {
        start: toIsoDate(interval.start),
        end: doc.period?.end ? toIsoDate(interval.end) : null,
    };
}

function gapRoundingWindow(args: {
    window: ParsedWindow;
    firstIntervalStart: Date;
}): DateInterval {
    const { window, firstIntervalStart } = args;
    return {
        start: window.start ?? firstIntervalStart,
        end: window.end ?? OPEN_INTERVAL_END,
    };
}

// A month with any covered day counts as covered, so gaps round inward to
// whole uncovered months. Edges at the window boundary stay exact: there is
// no neighboring coverage to absorb the partial month.
function roundGapToUncoveredMonths(args: {
    gap: DateInterval;
    window: DateInterval;
    nextDocStart: Date | null;
}): DateInterval | null {
    const { gap, window, nextDocStart } = args;
    let { start, end } = gap;
    const isStartMidMonth = !isEqual(start, startOfMonth(start));
    if (isStartMidMonth && !isEqual(start, window.start)) {
        start = addMonths(startOfMonth(start), 1);
    }
    if (nextDocStart && !isEqual(nextDocStart, startOfMonth(nextDocStart))) {
        end = subDays(startOfMonth(nextDocStart), 1);
    }
    if (isAfter(start, end)) {
        return null;
    }
    return { start, end };
}

function clipDocs(args: {
    docs: CollectedExtraction[];
    requirementPeriod: Period | null;
}): { clipped: ClippedDoc[]; unmatched: UnmatchedDoc[] } {
    const { docs, requirementPeriod } = args;
    const unmatched: UnmatchedDoc[] = [];
    const clipped: ClippedDoc[] = [];

    for (const doc of docs) {
        if (!doc.period || !doc.period.start) {
            unmatched.push(coverageUnmatchedDoc(doc, "no_transactions"));
            continue;
        }
        const clip = clipDocToOptionalWindow(doc.period, requirementPeriod);
        if (clip === "outside_period") {
            unmatched.push(coverageUnmatchedDoc(doc, "outside_period"));
            continue;
        }
        clipped.push({ doc, interval: clip });
    }

    clipped.sort((a, b) => {
        const diff = a.interval.start.getTime() - b.interval.start.getTime();
        return diff !== 0 ? diff : a.doc.documentId - b.doc.documentId;
    });

    return { clipped, unmatched };
}

function buildCoverageRows(args: {
    key: RequirementKey;
    clipped: ClippedDoc[];
    window: ParsedWindow;
    unmatched: UnmatchedDoc[];
}): { rows: CoverageRow[]; unmatched: UnmatchedDoc[] } {
    const { key, clipped, window, unmatched } = args;
    const roundingWindow = gapRoundingWindow({
        window,
        firstIntervalStart: clipped[0].interval.start,
    });

    const rows: CoverageRow[] = [];
    let cursor: Date | null = window.start;
    for (const { doc, interval } of clipped) {
        if (cursor !== null && isBefore(interval.start, cursor)) {
            unmatched.push(coverageUnmatchedDoc(doc, "overlap"));
            continue;
        }
        if (cursor !== null && isAfter(interval.start, cursor)) {
            const gap = roundGapToUncoveredMonths({
                gap: { start: cursor, end: subDays(interval.start, 1) },
                window: roundingWindow,
                nextDocStart: interval.start,
            });
            if (gap) {
                rows.push(
                    coverageAwaitingRow({
                        key,
                        start: gap.start,
                        end: gap.end,
                    }),
                );
            }
        }
        rows.push(
            coverageMatchedRow({
                key,
                doc,
                period: matchedPeriodFromInterval(doc, interval),
            }),
        );
        cursor = addDays(interval.end, 1);
    }
    if (window.end && cursor !== null && !isAfter(cursor, window.end)) {
        const gap = roundGapToUncoveredMonths({
            gap: { start: cursor, end: window.end },
            window: roundingWindow,
            nextDocStart: null,
        });
        if (gap) {
            rows.push(
                coverageAwaitingRow({ key, start: gap.start, end: gap.end }),
            );
        }
    }

    return { rows, unmatched };
}

function coverContinuous(args: {
    key: RequirementKey;
    docs: CollectedExtraction[];
    requirementPeriod: Period | null;
}): ContinuousCoverage {
    const { key, docs, requirementPeriod } = args;
    const window = parseOptionalPeriodToWindow(requirementPeriod);

    if (docs.length === 0) {
        return {
            rows: [
                coverageRequirementAwaitingRow(
                    key,
                    awaitingPeriodFromWindow(window),
                ),
            ],
            unmatched: [],
            isCovered: false,
        };
    }

    const { clipped, unmatched: clipUnmatched } = clipDocs({
        docs,
        requirementPeriod,
    });

    if (clipped.length === 0) {
        return {
            rows: [
                coverageRequirementAwaitingRow(
                    key,
                    awaitingPeriodFromWindow(window),
                ),
            ],
            unmatched: clipUnmatched,
            isCovered: false,
        };
    }

    const { rows, unmatched } = buildCoverageRows({
        key,
        clipped,
        window,
        unmatched: clipUnmatched,
    });

    return {
        rows,
        unmatched,
        isCovered: rows.every((row) => row.status !== "awaiting"),
    };
}

export function assessContinuous(args: {
    key: RequirementKey;
    period: Period | null;
    docs: CollectedExtraction[];
}): ContinuousCoverage {
    const { key, period, docs } = args;
    return coverContinuous({ key, docs, requirementPeriod: period });
}
